import { collection, getDocs } from "firebase/firestore";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";

import { db } from "integrations/firebase/firestore";

import {
  speakerSchema,
  trackSchema,
  type Speaker,
  type Track,
} from "../event-experience/schemas";

import {
  cleanValue,
  firstName,
  parseTemplate,
  PLACEHOLDERS,
  wouldTruncate,
  type PlaceholderKey,
  type PlaceholderValues,
} from "./announcement-placeholders";
import { championName } from "./track-champion";
import {
  attendeeRecordSchema,
  type AttendeeRecord,
} from "./use-attendee-records";

/**
 * Who a personalised announcement reaches and what each of them would see
 * (TNG-95), for the composer's preview and warnings.
 *
 * The same sources the backend fills placeholders from
 * (firestore_announcement_recipients.py): every member document, the roster
 * row it links to, the track it names and that track's champion. One thing
 * the admin cannot read is another person's account profile, which is where
 * the backend finds the name of a member with no roster row (an organiser,
 * usually), or with a row whose name is blank. Those members are listed, the
 * name placeholders are never counted as missing for anyone, and a member
 * without a row is left out of the other counts.
 */

const memberSchema = z.object({
  id: z.string(),
  attendeeId: z.string().optional().catch(undefined),
  trackId: z.string().optional().catch(undefined),
  isEventAdmin: z.boolean().optional().catch(undefined),
});
export type RecipientMember = z.infer<typeof memberSchema>;

export interface AnnouncementRecipient {
  uid: string;
  /**
   * False when the roster gives no name for this member: no row, or a row
   * whose name is blank. The backend then names them from their profile,
   * which the admin cannot read, so the preview cannot show it and the
   * missing-value counts leave them out.
   */
  hasRosterRow: boolean;
  /**
   * True when the member links to a roster row that exists, named or not.
   * The track, room, question, champion and dinner table counts use this:
   * a blank name does not make those values any less missing.
   */
  hasRow: boolean;
  label: string;
  values: PlaceholderValues;
}

export function buildRecipients(
  members: RecipientMember[],
  attendees: AttendeeRecord[],
  tracks: Track[],
  speakers: Speaker[],
  /**
   * attendeeId -> dinner table name in the chosen seating's published copy
   * (TNG-94), the same source the backend fills {dinner_table} from.
   */
  dinnerTables: Map<string, string> = new Map(),
): AnnouncementRecipient[] {
  const attendeesById = new Map(attendees.map((a) => [a.id, a]));
  const tracksById = new Map(tracks.map((t) => [t.id, t]));
  const speakersById = new Map(speakers.map((s) => [s.id, s]));
  const recipients = members.map((member) => {
    const row = member.attendeeId
      ? attendeesById.get(member.attendeeId)
      : undefined;
    const track = member.trackId ? tracksById.get(member.trackId) : undefined;
    const name = cleanValue(row?.name);
    const values: PlaceholderValues = {
      first_name: firstName(name),
      full_name: name,
      track: cleanValue(track?.name),
      room: cleanValue(track?.room),
      question: cleanValue(track?.question),
      champion: track ? championName(track, attendeesById, speakersById) : null,
      dinner_table: member.attendeeId
        ? cleanValue(dinnerTables.get(member.attendeeId))
        : null,
    };
    return {
      uid: member.id,
      hasRosterRow: name !== null,
      hasRow: row !== undefined,
      label: name
        ? name
        : member.isEventAdmin
          ? "Organiser (name from their profile)"
          : "Member (name from their profile)",
      values,
    };
  });
  // People with a roster row first, by name, so the picker reads like the
  // attendee list; the members named only by their profile after them.
  return recipients.sort((a, b) =>
    a.hasRosterRow !== b.hasRosterRow
      ? a.hasRosterRow
        ? -1
        : 1
      : a.label.localeCompare(b.label),
  );
}

// The name placeholders have no entry: a member whose roster row gives no
// name is named from their profile, so nobody is ever missing one.
type NameKey = "first_name" | "full_name";
const NAME_KEYS: ReadonlySet<PlaceholderKey> = new Set<NameKey>([
  "first_name",
  "full_name",
]);

const REASONS: Record<Exclude<PlaceholderKey, NameKey>, string> = {
  track: "have no track",
  room: "have no room for their track",
  question: "have no theme question for their track",
  champion: "have no champion for their track",
  dinner_table: "have no dinner table in the chosen seating",
};

export interface MissingValueWarning {
  key: PlaceholderKey;
  missing: number;
  total: number;
  /** What those recipients see instead: the inline fallback or the default. */
  shown: string;
  text: string;
}

/**
 * Whether this recipient's copy will show the fallback for `key`.
 *
 * Only for members with a roster row: one without has no track or seat the
 * admin can see. Never for a name placeholder: the backend fills a blank or
 * missing roster name from the member's profile.
 */
function isMissing(recipient: AnnouncementRecipient, key: PlaceholderKey) {
  return (
    !NAME_KEYS.has(key) &&
    recipient.hasRow &&
    !cleanValue(recipient.values[key])
  );
}

/**
 * One warning per placeholder that some recipients have no value for.
 *
 * Counted over members with a roster row, named or not. Name placeholders
 * are never warned about (see isMissing). Non-blocking: everyone still gets
 * the message.
 */
export function missingValueWarnings(
  title: string,
  body: string,
  recipients: AnnouncementRecipient[],
): MissingValueWarning[] {
  const parts = [
    ...parseTemplate(title, false).parts,
    ...parseTemplate(body, false).parts,
  ].filter((part) => typeof part !== "string");
  const counted = recipients.filter((recipient) => recipient.hasRow);
  const warnings: MissingValueWarning[] = [];
  for (const spec of PLACEHOLDERS) {
    if (NAME_KEYS.has(spec.key)) continue;
    const key = spec.key as Exclude<PlaceholderKey, NameKey>;
    const first = parts.find((part) => part.key === key);
    if (!first) continue;
    const missing = counted.filter((recipient) =>
      isMissing(recipient, key),
    ).length;
    if (missing === 0) continue;
    const shown = first.fallback ?? spec.fallback;
    warnings.push({
      key: spec.key,
      missing,
      total: counted.length,
      shown,
      text: `${missing} of ${counted.length} ${counted.length === 1 ? "recipient" : "recipients"} ${REASONS[key]} and will see “${shown}” for {${spec.key}}.`,
    });
  }
  return warnings;
}

/** How many recipients' copies would be cut to fit, title or body. */
export function truncatedCount(
  title: string,
  body: string,
  recipients: AnnouncementRecipient[],
  maxTitle: number,
  maxBody: number,
): number {
  return recipients.filter(
    (recipient) =>
      wouldTruncate(title, recipient.values, maxTitle) ||
      wouldTruncate(body, recipient.values, maxBody),
  ).length;
}

/**
 * Who the preview shows first: somebody who is missing a value the message
 * uses, since that is the copy most worth checking, else the first person.
 */
export function defaultPreviewRecipient(
  title: string,
  body: string,
  recipients: AnnouncementRecipient[],
): AnnouncementRecipient | undefined {
  const keys = new Set(
    [...parseTemplate(title, false).parts, ...parseTemplate(body, false).parts]
      .filter((part) => typeof part !== "string")
      .map((part) => part.key),
  );
  return (
    recipients.find((recipient) =>
      [...keys].some((key) => isMissing(recipient, key)),
    ) ?? recipients[0]
  );
}

export type RecipientsState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; recipients: AnnouncementRecipient[] };

interface RecipientSources {
  members: RecipientMember[];
  attendees: AttendeeRecord[];
  tracks: Track[];
  speakers: Speaker[];
}

const publishedTableRowSchema = z.object({
  id: z.string(),
  name: z.string().catch(""),
  attendeeIds: z.array(z.string()).catch([]),
});

/**
 * The recipients, read once `enabled` turns true (a message with a
 * placeholder in it) and kept from then on. The roster, members, tracks and
 * speakers are four reads nobody writing "Dinner at 19:00" needs. Read once:
 * the backend reads its own copy at the send time, and the confirm dialog
 * says so for a scheduled send.
 *
 * With `seatingId`, that seating's published dinner tables are read too, so
 * {dinner_table} previews as each person would see it (TNG-94). A seating
 * that is not published has no published tables, and everyone shows the
 * fallback, which is also what the warning then says.
 */
export function useAnnouncementRecipients(
  eventId: string,
  enabled: boolean,
  seatingId?: string | null,
): RecipientsState {
  const [wanted, setWanted] = useState(enabled);
  const [state, setState] = useState<
    | { status: "idle" }
    | { status: "loading" }
    | { status: "error"; error: string }
    | { status: "success"; sources: RecipientSources }
  >({ status: "idle" });
  const [dinnerTables, setDinnerTables] = useState<Map<string, string>>(
    () => new Map(),
  );

  useEffect(() => {
    if (enabled) setWanted(true);
  }, [enabled]);

  useEffect(() => {
    if (!wanted) return;
    let cancelled = false;
    setState({ status: "loading" });
    const read = <T>(name: string, schema: z.ZodType<T>) =>
      getDocs(collection(db, "events", eventId, name)).then((snapshot) => {
        const data: T[] = [];
        snapshot.forEach((doc) => {
          const result = schema.safeParse({ id: doc.id, ...doc.data() });
          if (result.success) data.push(result.data);
        });
        return data;
      });
    Promise.all([
      read("members", memberSchema),
      read("attendees", attendeeRecordSchema),
      read("tracks", trackSchema),
      read("speakers", speakerSchema),
    ])
      .then(([members, attendees, tracks, speakers]) => {
        if (cancelled) return;
        setState({
          status: "success",
          sources: { members, attendees, tracks, speakers },
        });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState({
          status: "error",
          error: error instanceof Error ? error.message : "Failed to load",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [eventId, wanted]);

  useEffect(() => {
    setDinnerTables(new Map());
    if (!wanted || !seatingId) return;
    let cancelled = false;
    getDocs(
      collection(db, "events", eventId, "seatings", seatingId, "published"),
    )
      .then((snapshot) => {
        if (cancelled) return;
        const tables = new Map<string, string>();
        snapshot.forEach((doc) => {
          const result = publishedTableRowSchema.safeParse({
            id: doc.id,
            ...doc.data(),
          });
          if (!result.success) return;
          for (const attendeeId of result.data.attendeeIds)
            tables.set(attendeeId, result.data.name);
        });
        setDinnerTables(tables);
      })
      // Without it the preview shows the fallback, which is what a guest
      // without a dinner table sees anyway.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [eventId, wanted, seatingId]);

  return useMemo<RecipientsState>(() => {
    if (state.status !== "success") return state;
    const { members, attendees, tracks, speakers } = state.sources;
    return {
      status: "success",
      recipients: buildRecipients(
        members,
        attendees,
        tracks,
        speakers,
        dinnerTables,
      ),
    };
  }, [state, dinnerTables]);
}
